// Moment.js
//
// A single full-screen "Moment" (short-video / image post) card,
// styled to match the For-You feed reference design:
//   - top-right profile avatar with the For You | Following tabs below it
//   - full-bleed media background
//   - bottom-left author line, caption, hashtags, sound row
//   - right-side vertical action rail (comment / like / view / care / share)
//   - spinning sound disc + upload/repost button under the action rail
//
// This component is presentation + interaction only — it takes post data
// and action callbacks as props so it can be dropped into GroupChat.js /
// MobileAppLayout the same way <Post /> is used elsewhere.
//
// IMPORTANT: the top bar (tab switcher + avatar) is ALWAYS rendered,
// even when there are no posts yet — only the media/caption/action-rail
// block depends on an actual post being present. This lets the user
// still switch between "For You" and "Following" while either list is
// empty, instead of seeing a completely blank screen.
//
// MEDIA SHAPE:
//   Media posts now store multiple media items in `post.media[]`:
//     media: [{ type, url, mimeType, size, duration, thumbnail,
//               width, height, aspectRatio }]
//   Moment displays the FIRST media item of the current post.
//   A legacy single `media` object is still supported.
//
// UPLOAD FLOW:
//   1. Tapping the upload FAB opens a small "Photo / Video" picker menu.
//   2. Choosing a type opens the matching native file picker
//      (accept="image/*" or accept="video/*").
//   3. Picked files are resized (images) and turned into local
//      object-URL previews, shown in a bottom preview sheet via the
//      same <FeedMediaComposer /> + MediaGrid used by Feed.js — nothing
//      is uploaded yet. More files can also be added from the sheet's
//      own "📎 Media (n/4)" picker, same as the Feed composer.
//   4. The user can add a caption, then tap "Post" to actually upload
//      the files and create ONE post carrying all files as `media[]`,
//      or "Cancel" to discard the selection.
//
// TABS (left -> right):  STEM | For You | Following | Series
//   - For You / Following behave exactly as before and show EVERY post
//     type (standard, series and stem posts all appear there).
//   - STEM behaves like For You (vertical swipe), but only shows
//     posts with type === "stem".
//   - Series is a 2-axis feed built from posts with type === "series":
//       vertical swipe   : up = next series, down = previous series
//       horizontal swipe : left = next episode, right = previous episode
//     Series -> Season -> Episode. Episodes are ordered by
//     (seasonNumber, episodeNumber), so swiping past the last episode of
//     Season 1 lands on Season 2 / Episode 1 (and back again).
//
// CREATE FLOW (upload button):
//   Standard | Series | STEM
//     Series -> "Create new series" (asks for a title, starts at S1 E1)
//            -> "Add to series"     (pick one of YOUR series, choose the
//                                    season / episode, defaults to the
//                                    next episode number)
//   then Photo | Video, then the same preview sheet as before.
//
// Usage:
//   <Moment
//     mediaPosts={globalMediaLivePosts}
//     followingMediaPosts={followingMediaPosts}
//     commentedMediaPosts={commentedMediaPosts}
//     myMediaPosts={myMediaPosts}
//     mediaNotifications={mediaNotifications}
//     currentUser={currentUser}
//     avatarUrl={avatarUrl}
//     onCreateMediaPost={mediaSocket.mediaCreatePost}
//     onLikeMediaPost={mediaSocket.mediaLikePost}
//     onAddMediaComment={mediaSocket.mediaAddComment}
//     mediaCommentsByPost={mediaCommentsByPost}
//     users={users}
//     onTrackMediaView={mediaSocket.mediaTrackView}
//     onLikeMediaComment={mediaSocket.mediaLikeComment}
//     onClearMediaNotifications={mediaSocket.mediaClearNotifications}
//     onSupportMediaPost={mediaSocket.mediaSupportPost}
//     onUploadMedia={uploadFeedMedia}
//   />

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  HeartIcon,
  CommentIcon,
  EyeIcon,
  SupportIcon,
  ShareIcon,
  MusicIcon,
} from "../components/mediaIcon";
import { v4 as uuidv4 } from "uuid";
import { resizeImage } from "../utils/resizeImage";
import FeedMediaComposer from "../components/FeedMediaComposer";
import MediaComment from "../cards/mediaComment";
import {
  Sound,
  getPostSound,
  formatSoundLabel,
  toSoundPayload,
} from "./sound";

/* =========================================================
   🔢 NUMBER FORMAT HELPER
========================================================= */

const formatCount = (value) => {
  const num = Number(value) || 0;

  if (num >= 1_000_000) {
    return `${(num / 1_000_000).toFixed(
      num % 1_000_000 === 0 ? 0 : 1
    )}M`;
  }

  if (num >= 1_000) {
    return `${(num / 1_000).toFixed(
      num % 1_000 === 0 ? 0 : 1
    )}K`;
  }

  return `${num}`;
};

/* =========================================================
   ☁️ UPLOAD ICON (not part of mediaIcon.js — bottom-right FAB)
========================================================= */

function UploadIcon({ size = 22, color = "#fff" }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M7 16.5A4.5 4.5 0 0 1 8.2 7.6 5.5 5.5 0 0 1 18.8 9 4 4 0 0 1 18 17H7.5"
        stroke={color}
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12 20v-7M9.2 15.8 12 13l2.8 2.8"
        stroke={color}
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/* =========================================================
   🖼️ PHOTO ICON (media-type picker menu)
========================================================= */

function PhotoIcon({ size = 20, color = "#fff" }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect
        x="3"
        y="4"
        width="18"
        height="16"
        rx="2.5"
        stroke={color}
        strokeWidth="1.7"
      />
      <circle cx="8.5" cy="9.5" r="1.5" fill={color} />
      <path
        d="M4 17.5 9 12l3.5 3.5L15 13l5 5"
        stroke={color}
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/* =========================================================
   🎥 VIDEO ICON (media-type picker menu)
========================================================= */

function VideoIcon({ size = 20, color = "#fff" }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect
        x="3"
        y="6"
        width="13"
        height="12"
        rx="2.5"
        stroke={color}
        strokeWidth="1.7"
      />
      <path
        d="m16.5 10.3 4-2.3v8l-4-2.3"
        stroke={color}
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/* =========================================================
   ✖️ CLOSE ICON (remove pending item / dismiss sheet)
========================================================= */

function CloseIcon({ size = 16, color = "#fff" }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M6 6l12 12M18 6 6 18"
        stroke={color}
        strokeWidth="2.2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function formatTime(sec = 0) {
  const t = Math.max(0, Math.floor(sec || 0));
  return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, "0")}`;
}

/* =========================================================
   🗂️ TABS + SERIES HELPERS
========================================================= */

// Tab order: STEM | For You | Following | Series
const MOMENT_TABS = [
  { id: "stem", label: "STEM" },
  { id: "forYou", label: "For You" },
  { id: "following", label: "Following" },
  { id: "series", label: "Series" },
];

const EMPTY_LABELS = {
  stem: "No STEM moments yet.",
  forYou: "No moments yet.",
  following: "No moments yet.",
  series: "No series yet.",
};

// Merge several post lists into one, keeping first-seen order, no duplicates.
function mergeUniquePosts(lists) {
  const byId = new Map();
  lists.forEach((list) => {
    (list || []).forEach((p) => {
      if (p?.postId && !byId.has(p.postId)) byId.set(p.postId, p);
    });
  });
  return [...byId.values()];
}

// Series -> Season -> Episode.
// Returns [{ seriesId, title, posts, seasons }] where `posts` is the flat,
// ordered episode list (season asc, then episode asc) and `seasons` is the
// list of season numbers. Series keep the order in which they first show up
// in the feed.
function groupSeries(posts) {
  const map = new Map();

  (posts || []).forEach((p) => {
    if (p?.type !== "series" || !p.seriesId) return;

    let group = map.get(p.seriesId);
    if (!group) {
      group = { seriesId: p.seriesId, title: null, posts: [], seasons: [] };
      map.set(p.seriesId, group);
    }
    group.posts.push(p);
    if (!group.title && p.seriesTitle) group.title = p.seriesTitle;
  });

  map.forEach((group) => {
    group.posts.sort(
      (a, b) =>
        (a.seasonNumber || 1) - (b.seasonNumber || 1) ||
        (a.episodeNumber || 0) - (b.episodeNumber || 0)
    );
    group.seasons = [...new Set(group.posts.map((p) => p.seasonNumber || 1))];
  });

  return [...map.values()];
}

// Next free episode number inside one season of a series group.
function nextEpisodeNumber(group, season) {
  const inSeason = (group?.posts || []).filter(
    (p) => (p.seasonNumber || 1) === season
  );
  return Math.max(0, ...inSeason.map((p) => p.episodeNumber || 0)) + 1;
}

// Is this post authored by the logged-in user? (used for "Add to series",
// which may only list the viewer's own series)
function isPostByUser(p, chatId, username) {
  const pid = p?.author?.chatId ?? p?.chatId ?? p?.authorId;
  if (chatId != null && pid != null && String(pid) === String(chatId)) {
    return true;
  }
  const un = String(p?.author?.username || p?.authorUsername || "")
    .replace(/^@/, "")
    .toLowerCase();
  const me = String(username || "").replace(/^@/, "").toLowerCase();
  return !!me && un === me;
}

/* =========================================================
   🎬 MOMENT
========================================================= */

export default function Moment({
  mediaPosts = [],
  followingMediaPosts = [],
  commentedMediaPosts = [],
  myMediaPosts = [],
  mediaNotifications = [],

  isLiked = false,
  isSupported = false,

  onCreateMediaPost,
  onLikeMediaPost,
  onAddMediaComment,
  onTrackMediaView,
  onLikeMediaComment,
  onClearMediaNotifications,
  onSupportMediaPost,

  onUploadMedia,
  onAvatarClick, // author avatar (above the heart) -> onAvatarClick(author)
  onMyAvatarClick, // viewer's own avatar (top-right corner)
  onAuthorClick,
  onSoundClick,

  // The logged-in user — needed so a newly created post can carry
  // chatId / authorUsername (mediaCreatePost requires both to emit and
  // to build its optimistic post).
  currentUser,

  // currentUser has no flat chatId/username/displayName fields (those
  // live nested under currentUser.chatInfo[]) — the caller (GroupChat.js,
  // via DesktopSocialLayout/MobileAppLayout) derives and passes these
  // down explicitly, so they must be read from props, not currentUser.
  myChatId,
  myUserName,
  myDisplayName,

  // Used by the comment page: `users` resolves comment-author avatars and
  // `mediaCommentsByPost` ({ [postId]: comment[] }) is the live comment
  // store kept up to date by mediaOnNewComment / mediaOnCommentCountUpdate /
  // mediaOnCommentLikeUpdate in GroupChat.js.
  users,
  mediaCommentsByPost = {},
  onSupportMediaComment,
  onViewMediaCommentEarnings,
  onTrackMediaCommentView,

  // Fallback avatar (current user) shown in the top-right corner when
  // there is no active post to derive an author avatar from. Passed as
  // `avatarUrl` from DesktopSocialLayout / MobileAppLayout.
  avatarUrl,

  // Passed by MobileAppLayout / DesktopSocialLayout. Moment stays mounted
  // (hidden with visibility:hidden) when another tab is open, so it must
  // use these to know when it is actually on screen.
  activeTab,
  TABS,
  suspended = false,

  className,
  style,
}) {
  /* =======================================================
     🧠 LOCAL STATE
  ======================================================= */

  const [captionExpanded, setCaptionExpanded] = useState(false);
  const [momentTab, setMomentTab] = useState("forYou");
  const [isUploadingMedia, setIsUploadingMedia] = useState(false);
  const videoRef = useRef(null);

  // Starts unmuted: tapping the Moment tab counts as the user gesture.
  // If the browser still blocks it, the effect below falls back to muted.
  const [isMuted, setIsMuted] = useState(false);

  // Play-time display (TikTok-style)
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isScrubbing, setIsScrubbing] = useState(false);

  // Tap-to-pause state + measured overlap with the mobile bottom tab bar
  const [isPaused, setIsPaused] = useState(false);
  const userPausedRef = useRef(false); // true only when the USER tapped pause
  const screenRef = useRef(null);
  const [bottomInset, setBottomInset] = useState(0);

  // Sound page (opened from the sound row / spinning disc, or from the
  // "Add sound" button in the preview sheet):
  //   null                              -> closed
  //   { sound, mode: "detail" }         -> details of one sound
  //   { sound: null, mode: "pick" }     -> browse / pick a sound
  const [soundPage, setSoundPage] = useState(null);

  // Sound the user chose to attach to the post they are about to create.
  const [selectedSound, setSelectedSound] = useState(null);

  // "Photo / Video" bottom sheet shown when the upload button is tapped.
  // While it is open the video (and any sound track) is paused.
  const [showMediaTypeMenu, setShowMediaTypeMenu] = useState(false);
  // True from the moment Photo / Video is tapped until the native file
  // picker closes (a file is chosen or the picker is cancelled).
  const [isPickingFile, setIsPickingFile] = useState(false);
  // Separate pickers so the native file dialog is pre-filtered to the
  // type the user chose from the Photo / Video menu.
  const photoInputRef = useRef(null);
  const videoInputRef = useRef(null);

  // Files the user has picked but not yet posted — mirrors Feed.js's
  // composer state exactly (mediaFiles/mediaPreview/uploadProgress) so
  // the same <FeedMediaComposer /> (MediaGrid + per-file progress bar)
  // can be reused here for an identical preview.
  const [mediaFiles, setMediaFiles] = useState([]);
  const [mediaPreview, setMediaPreview] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
    const [pendingCaption, setPendingCaption] = useState("");

  // What kind of moment the user is creating: standard | series | stem.
  const [momentKind, setMomentKind] = useState("standard");
  // Which screen of the create sheet is showing:
  //   kind -> seriesMode -> (seriesNew | seriesPick -> seriesNumbers) -> media
  const [createStep, setCreateStep] = useState("kind");
  // Series details chosen in the create sheet.
  const [seriesDraft, setSeriesDraft] = useState({
    mode: null, // "new" | "existing"
    title: "",
    seriesId: null,
    season: 1,
    episode: 1,
  });
  // seriesId -> title for series created in this session (until the server
  // sends `seriesTitle` back on the posts themselves).
  const [localSeriesTitles, setLocalSeriesTitles] = useState({});
  // Series tab: seriesId -> postId of the episode currently shown.
  const [selectedEpisode, setSelectedEpisode] = useState({});

  // True only while the Moment tab is the one being shown (and no overlay
  // page such as the Sound page is covering it). Falls back to true if the
  // parent doesn't pass activeTab/TABS.
  // The video also stays paused while the native file picker is open
  // (isPickingFile) and while the preview sheet is open (mediaPreview).
  const isActive =
    !suspended &&
    !soundPage &&
    !showMediaTypeMenu &&
    !isPickingFile &&
    mediaPreview.length === 0 &&
    (TABS && activeTab !== undefined ? activeTab === TABS.MOMENT : true);

  // If the user dismisses the native picker without choosing anything, the
  // input fires a native "cancel" event (React has no onCancel for inputs).
  useEffect(() => {
    const photo = photoInputRef.current;
    const video = videoInputRef.current;
    const onCancel = () => setIsPickingFile(false);
    photo?.addEventListener("cancel", onCancel);
    video?.addEventListener("cancel", onCancel);
    return () => {
      photo?.removeEventListener("cancel", onCancel);
      video?.removeEventListener("cancel", onCancel);
    };
  }, []);

  // Comment page (opened from the comment icon in the action rail).
  const [showCommentSheet, setShowCommentSheet] = useState(false);

  /* =======================================================
     📚 ACTIVE POST LIST (driven by momentTab)

     commentedMediaPosts / myMediaPosts are accepted for future
     tabs (e.g. "Comments" / "Mine") — the current tab switcher
     only toggles between For You and Following.
  ======================================================= */

  const [currentIndex, setCurrentIndex] = useState(0);

  // STEM / Series tabs are built from every loaded list so a series or stem
  // post shows up there no matter where it arrived from (global, following
  // or the viewer's own posts). For You / Following still show everything.
  const mergedPosts = useMemo(
    () => mergeUniquePosts([mediaPosts, followingMediaPosts, myMediaPosts]),
    [mediaPosts, followingMediaPosts, myMediaPosts]
  );

  const stemPosts = useMemo(
    () => mergedPosts.filter((p) => p.type === "stem"),
    [mergedPosts]
  );

  const seriesList = useMemo(() => groupSeries(mergedPosts), [mergedPosts]);

  // For the Series tab the vertical list is "one entry per series", and each
  // entry is that series' CURRENT episode. That way `post`, goTo() and the
  // index-clamping effects below all work unchanged.
  const activePosts = useMemo(() => {
    if (momentTab === "following") return followingMediaPosts;
    if (momentTab === "stem") return stemPosts;
    if (momentTab === "series") {
      return seriesList.map(
        (g) =>
          g.posts.find((p) => p.postId === selectedEpisode[g.seriesId]) ||
          g.posts[0]
      );
    }
    return mediaPosts;
  }, [
    momentTab,
    followingMediaPosts,
    mediaPosts,
    stemPosts,
    seriesList,
    selectedEpisode,
  ]);

  const currentSeries =
    momentTab === "series" ? seriesList[currentIndex] || null : null;

  useEffect(() => {
    setCurrentIndex(0);
  }, [momentTab]);

  // Jump to a specific post (used when a tile on the Sound page is tapped
  // and that post lives in the other tab). Declared after the reset effect
  // above so it wins when both run for the same tab change.
  const pendingJumpRef = useRef(null);
  useEffect(() => {
    if (!pendingJumpRef.current) return;
    const idx = activePosts.findIndex((p) => p.postId === pendingJumpRef.current);
    if (idx >= 0) {
      setCurrentIndex(idx);
      pendingJumpRef.current = null;
    }
  }, [activePosts, momentTab]);

  const post = activePosts[currentIndex] || null;

  // A post that uses a library / someone else's sound plays THAT audio and
  // its own video is muted. Posts with no sound (or an "original" one)
  // simply play their own audio.
  const postSound = post?.sound || null;
  const hasCustomSound = !!(postSound?.url && !postSound.isOriginal);

  // Every loaded post, de-duplicated — the Sound page uses this to list the
  // videos that use a sound and to build the sound picker.
  const allPosts = useMemo(() => {
    const byId = new Map();
    [mediaPosts, followingMediaPosts, myMediaPosts, commentedMediaPosts].forEach(
      (list) => {
        (list || []).forEach((p) => {
          if (p?.postId && !byId.has(p.postId)) byId.set(p.postId, p);
        });
      }
    );
    return [...byId.values()];
  }, [mediaPosts, followingMediaPosts, myMediaPosts, commentedMediaPosts]);

  const seriesTitleOf = (group) =>
    group?.title || localSeriesTitles[group?.seriesId] || "Untitled series";

  // Series the viewer owns — the "Add to series" list.
  const mySeries = useMemo(
    () =>
      groupSeries(allPosts.filter((p) => isPostByUser(p, myChatId, myUserName))),
    [allPosts, myChatId, myUserName]
  );

  // Where we are inside the current series (drives the badge + dots).
  const seriesNav = (() => {
    if (momentTab !== "series" || !currentSeries || !post) return null;
    const idx = currentSeries.posts.findIndex((p) => p.postId === post.postId);
    return {
      title: seriesTitleOf(currentSeries),
      season: post.seasonNumber || 1,
      episode: post.episodeNumber || idx + 1,
      idx,
      total: currentSeries.posts.length,
    };
  })();

  // Keep the index valid if the list shrinks (e.g. a failed optimistic
  // post is removed).
  useEffect(() => {
    if (currentIndex > 0 && currentIndex >= activePosts.length) {
      setCurrentIndex(Math.max(activePosts.length - 1, 0));
    }
  }, [activePosts.length, currentIndex]);

  /* =======================================================
     ⬆️⬇️ POST NAVIGATION (wheel / swipe / arrow keys)
  ======================================================= */

  const goTo = (delta) =>
    setCurrentIndex((i) =>
      Math.min(Math.max(i + delta, 0), Math.max(activePosts.length - 1, 0))
    );

  // Series tab only: move to the next / previous episode (crossing season
  // boundaries, because `currentSeries.posts` is ordered season -> episode).
  const goEpisode = (delta) => {
    if (momentTab !== "series" || !currentSeries || !post) return;
    const idx = currentSeries.posts.findIndex((p) => p.postId === post.postId);
    const next = Math.min(
      Math.max(idx + delta, 0),
      currentSeries.posts.length - 1
    );
    if (next === idx) return;
    setSelectedEpisode((prev) => ({
      ...prev,
      [currentSeries.seriesId]: currentSeries.posts[next].postId,
    }));
  };

  const wheelLockRef = useRef(false);
  const touchStartRef = useRef(null);

  const handleWheel = (e) => {
    if (showCommentSheet) return; // scrolling comments must not change post
    if (wheelLockRef.current) return;

    // Series tab: a mostly-horizontal wheel / trackpad swipe = episodes
    const horizontal =
      momentTab === "series" && Math.abs(e.deltaX) > Math.abs(e.deltaY);
    const magnitude = horizontal ? Math.abs(e.deltaX) : Math.abs(e.deltaY);
    if (magnitude < 30) return;

    wheelLockRef.current = true;
    if (horizontal) goEpisode(e.deltaX > 0 ? 1 : -1);
    else goTo(e.deltaY > 0 ? 1 : -1);
    setTimeout(() => {
      wheelLockRef.current = false;
    }, 500);
  };

  const handleTouchStart = (e) => {
    if (showCommentSheet) {
      touchStartRef.current = null;
      return;
    }
    touchStartRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
    };
  };

  const handleTouchEnd = (e) => {
    const start = touchStartRef.current;
    if (!start) return;
    touchStartRef.current = null;

    const dx = start.x - e.changedTouches[0].clientX; // > 0 = finger moved left
    const dy = start.y - e.changedTouches[0].clientY; // > 0 = finger moved up

    // Series tab: a mostly-horizontal swipe changes episode
    //   swipe left  (<-) -> next episode
    //   swipe right (->) -> previous episode
    if (momentTab === "series" && Math.abs(dx) > Math.abs(dy)) {
      if (Math.abs(dx) > 50) goEpisode(dx > 0 ? 1 : -1);
      return;
    }

    // Vertical swipe (all tabs):
    //   swipe up   -> next post / next series
    //   swipe down -> previous post / previous series
    if (Math.abs(dy) > 50) goTo(dy > 0 ? 1 : -1);
  };

  const handleKeyDown = (e) => {
    if (showCommentSheet) return; // typing a comment must not change post
    const tag = e.target?.tagName;
    if (tag === "INPUT" || tag === "TEXTAREA") return; // typing a title / caption
    if (e.key === "ArrowDown") goTo(1);
    if (e.key === "ArrowUp") goTo(-1);
    if (momentTab === "series") {
      if (e.key === "ArrowRight") goEpisode(1);
      if (e.key === "ArrowLeft") goEpisode(-1);
    }
  };

  // React does not reliably update the `muted` attribute after mount,
  // so drive it through the ref. Declared after `post` on purpose.
  // Tries to play WITH sound; if the browser blocks it, falls back to muted.
  // Starts playback. Tries with sound first; if the browser blocks that,
  // switches to muted RIGHT AWAY (no waiting for a re-render) and plays.
  // Never overrides a pause the user made by tapping.
  const tryPlay = () => {
    const v = videoRef.current;
    if (!v || userPausedRef.current || !isActive) return;
    v.volume = 1;
    const p = v.play();
    if (p && typeof p.catch === "function") {
      p.catch((err) => {
        if (err && err.name === "AbortError") return; // src changed; onLoadedData retries
        if (!v.muted) {
          v.muted = true;
          setIsMuted(true);
          v.play().catch(() => {});
        }
      });
    }
  };

  // Play when the Moment tab becomes active (or the post changes while it
  // is active); pause when another tab is opened so audio never keeps
  // playing behind a hidden screen.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (isActive) {
      v.muted = isMuted || hasCustomSound;
      tryPlay();
    } else {
      v.pause();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, isMuted, hasCustomSound, post?.postId]);

  // Sound track for posts that use a library / someone else's sound.
  // Plays in step with the video: starts/stops with it, and restarts from
  // the sound's startTime whenever the video loops (see onTimeUpdate).
  const soundAudioRef = useRef(null);
  const lastVideoTimeRef = useRef(0);

  // true when the browser refused to start the sound track without a user
  // gesture (iOS Safari does this for every new <audio> element).
  const [soundBlocked, setSoundBlocked] = useState(false);

  const playSoundAudio = () => {
    const a = soundAudioRef.current;
    if (!a) return;
    const p = a.play();
    if (p && typeof p.then === "function") {
      p.then(() => setSoundBlocked(false)).catch((err) => {
        if (err && err.name === "AbortError") return;
        setSoundBlocked(true);
      });
    }
  };

  useEffect(() => {
    const a = soundAudioRef.current;
    if (!a) return;
    a.muted = isMuted;
    if (isActive && hasCustomSound && !isPaused) {
      playSoundAudio();
    } else {
      a.pause();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, isMuted, isPaused, hasCustomSound, post?.postId]);

  // If the sound was blocked, the user's next touch / key press starts it.
  // This must NOT touch the video or the mute state, so tapping the video
  // still pauses it on the very first tap.
  useEffect(() => {
    if (!soundBlocked || !isActive || !hasCustomSound || isPaused) return;
    const retry = () => playSoundAudio();
    const events = ["pointerup", "touchend", "keydown"];
    events.forEach((ev) => window.addEventListener(ev, retry, { once: true }));
    return () => events.forEach((ev) => window.removeEventListener(ev, retry));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [soundBlocked, isActive, hasCustomSound, isPaused, post?.postId]);

  // Reset the timer when the post changes
  useEffect(() => {
    setCurrentTime(0);
    setDuration(0);
    setIsScrubbing(false);
    setIsPaused(false);
    setSoundBlocked(false);
    userPausedRef.current = false;
  }, [post?.postId]);

  // Drag / tap on the progress bar to seek
  const seekFromEvent = (e) => {
    const v = isPhotoSound ? soundAudioRef.current : videoRef.current;
    if (!v || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 1);
    v.currentTime = ratio * duration;
    setCurrentTime(v.currentTime);
  };

  // Tap on video: pause / resume the whole video (not just the sound).
  // If autoplay had to fall back to muted, the first tap unmutes instead.
  const handleVideoTap = () => {
    // Photo + sound: tap toggles the sound (there is no <video>)
    if (isPhotoSound) {
      const pa = soundAudioRef.current;
      if (!pa) return;
      if (isMuted) {
        pa.muted = false;
        setIsMuted(false);
        playSoundAudio();
        return;
      }
      if (isPaused) {
        userPausedRef.current = false;
        setIsPaused(false);
        playSoundAudio(); // inside the tap gesture
      } else {
        userPausedRef.current = true;
        setIsPaused(true);
        pa.pause();
      }
      return;
    }

    const v = videoRef.current;
    if (!v) return;
    const a = soundAudioRef.current;

    // Autoplay had to fall back to muted: the first tap unmutes.
    if (isMuted) {
      v.muted = hasCustomSound; // a sound post keeps its own video muted
      if (a && hasCustomSound) {
        a.muted = false;
        if (!v.paused) playSoundAudio(); // inside the tap gesture
      }
      setIsMuted(false);
      return;
    }

    if (v.paused) {
      userPausedRef.current = false;
      v.play().catch(() => {});
      // start the sound inside the tap gesture so the browser allows it
      if (a && hasCustomSound) playSoundAudio();
    } else {
      userPausedRef.current = true;
      v.pause();
      if (a) a.pause(); // stop the sound right away, same as the video
    }
  };

  // Measure how far the fixed bottom tab bar overlaps the bottom of this
  // screen, and lift everything pinned to the bottom by exactly that much.
  useLayoutEffect(() => {
    const measure = () => {
      const screen = screenRef.current;
      const tab = document.querySelector(".bottom-tabs-bar");
      if (!screen || !tab || tab.offsetHeight === 0) {
        setBottomInset(0); // desktop, keyboard open, or no tab bar
        return;
      }
      const overlap =
        screen.getBoundingClientRect().bottom -
        tab.getBoundingClientRect().top;
      setBottomInset(Math.max(0, Math.round(overlap)));
    };

    measure();

    const ro =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    if (ro) {
      if (screenRef.current) ro.observe(screenRef.current);
      const tab = document.querySelector(".bottom-tabs-bar");
      if (tab) ro.observe(tab);
    }
    window.addEventListener("resize", measure);
    window.visualViewport?.addEventListener("resize", measure);
    const id = setInterval(measure, 500); // safety net if the tab bar mounts later

    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", measure);
      window.visualViewport?.removeEventListener("resize", measure);
      clearInterval(id);
    };
  }, []);

  /* =======================================================
     📦 DERIVED POST FIELDS
  ======================================================= */

  const author = post?.author || {};

  /*
   * Media posts now store multiple media items:
   *
   * media: [
   *   {
   *     type,
   *     url,
   *     mimeType,
   *     size,
   *     duration,
   *     thumbnail,
   *     width,
   *     height,
   *     aspectRatio
   *   }
   * ]
   *
   * Moment displays the first media item for the current post.
   * A legacy single media object is still supported.
   */
  const mediaItems = Array.isArray(post?.media)
    ? post.media
    : post?.media
      ? [post.media]
      : [];

  const media = mediaItems[0] || null;

  const mediaType = media?.type || post?.mediaType || null;
  // Photo post that plays a sound: the <audio> element acts as the "player"
  const isPhotoSound = !!post && mediaType !== "video" && hasCustomSound;
  const mediaUrl = media?.url || post?.coverThumbnail || null;
  const mediaThumbnail =
    media?.thumbnail ||
    post?.coverThumbnail ||
    null;

  const authorUsername =
    author.username ||
    post?.authorUsername ||
    "unknown";

  // AUTHOR avatar — shown above the heart in the action rail.
  // Prefers what's on the post, then looks the author up in `users`.
  const authorChatId = post?.chatId || author.chatId;
  const authorFromUsers = Array.isArray(users)
    ? users.find((u) => String(u?.chatId) === String(authorChatId))
    : null;

  const authorAvatarUrl =
    author.avatarUrl ||
    author.profileImageUrl ||
    post?.authorAvatarUrl ||
    authorFromUsers?.profileImageUrl ||
    authorFromUsers?.avatarUrl ||
    null;

  // VIEWER avatar — shown top-right (same as the profile image in Feed.js).
  const myAvatarUrl =
    avatarUrl ||
    currentUser?.profileImageUrl ||
    currentUser?.avatarUrl ||
    null;

  const myInitial = String(myDisplayName || myUserName || "U")
    .slice(0, 1)
    .toUpperCase();

  const caption = post?.caption || post?.text || "";

  const hashtags = useMemo(() => {
    if (Array.isArray(post?.hashtags) && post.hashtags.length) {
      return post.hashtags;
    }

    const matches = caption.match(/#[\w]+/g);
    return matches || [];
  }, [post?.hashtags, caption]);

  const captionBody = useMemo(() => {
    return caption.replace(/#[\w]+/g, "").trim();
  }, [caption]);

  const soundLabel = post?.sound?.isOriginal
    ? `Original Sound - ${authorUsername}`
    : post?.sound?.title
    ? `${post.sound.title}${
        post.sound.artist ? ` - ${post.sound.artist}` : ""
      }`
    : `Original Sound - ${authorUsername}`;

  const commentsCount = post?.commentsCount ?? 0;
  const likesCount = post?.likesCount ?? 0;
  const viewsCount = post?.viewsCount ?? 0;
  const supportsCount = post?.supportsCount ?? 0;
  const repostsCount = post?.repostsCount ?? 0;

  /* =======================================================
     👁️ AUTO VIEW TRACKING
  ======================================================= */

  useEffect(() => {
    if (!post?.postId || !mediaType) return;

    onTrackMediaView?.({
      postId: post.postId,
      mediaType,
    });
  }, [post?.postId, mediaType, onTrackMediaView]);

  /* =======================================================
     🧹 REVOKE PENDING OBJECT-URLS ON UNMOUNT
  ======================================================= */

  useEffect(() => {
    return () => {
      mediaPreview.forEach((item) => {
        if (item.url) URL.revokeObjectURL(item.url);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* =======================================================
     🎛️ HANDLERS — post interactions
  ======================================================= */

  const handleLike = () => {
    if (post?.postId) {
      onLikeMediaPost?.(post.postId);
    }
  };

  const handleSupport = (supportDetails = {}) => {
    if (post?.postId) {
      onSupportMediaPost?.({
        postId: post.postId,
        amount: supportDetails.amount,
        timestamp: supportDetails.timestamp ?? Date.now(),
        signature: supportDetails.signature,
      });
    }
  };

  const handleComment = () => {
    if (!post?.postId) return;
    setShowCommentSheet(true);
  };

  const handleCloseCommentSheet = () => {
    setShowCommentSheet(false);
  };

  const handleShare = async () => {
    if (!post?.postId) return;

    try {
      if (navigator.share) {
        await navigator.share({
          title: `@${authorUsername}`,
          text: captionBody,
          url: window.location.href,
        });
      } else {
        await navigator.clipboard.writeText(window.location.href);
      }
    } catch {}
  };

  // author avatar (above the heart)
  const handleAvatarClick = () => {
    if (onAvatarClick) onAvatarClick(author);
  };

  // viewer avatar (top-right)
  const handleMyAvatarClick = () => {
    if (onMyAvatarClick) onMyAvatarClick();
  };

  const handleAuthorClick = () => {
    if (onAuthorClick) onAuthorClick(author);
  };

  // Sound icon (row under the caption / spinning disc) -> Sound page.
  const handleSoundClick = () => {
    if (!post) return;
    if (onSoundClick) onSoundClick(post.sound);
    setSoundPage({ sound: getPostSound(post), mode: "detail" });
  };

  // "Add sound" in the preview sheet -> Sound picker.
  const handleOpenSoundPicker = () => {
    setSoundPage({ sound: null, mode: "pick" });
  };

  // "Use this sound" / "Use": remember the sound for the next post.
  // From the detail page the Sound page itself shows the Photo / Video
  // chooser and passes the choice back as `kind`; we then open the matching
  // file picker (still inside the tap gesture). From the picker the files
  // are already chosen, so just attach (kind is undefined).
  const handleUseSound = (sound, kind) => {
    setSelectedSound(sound || null);
    setSoundPage(null);
    setShowMediaTypeMenu(false);
        if (kind) setIsPickingFile(true); // keep the video paused while the picker is open
    if (kind === "photo") photoInputRef.current?.click();
    else if (kind === "video") videoInputRef.current?.click();
  };

  // Tile tapped on the Sound page -> jump to that post.
  const handleSelectSoundPost = (target) => {
    if (!target?.postId) return;
    setSoundPage(null);

    const inActive = activePosts.findIndex((p) => p.postId === target.postId);
    if (inActive >= 0) {
      setCurrentIndex(inActive);
      return;
    }

    // Not in the current tab -> look in For You, then Following.
    const candidates = ["forYou", "following"].filter((t) => t !== momentTab);
    for (const tab of candidates) {
      const list = tab === "following" ? followingMediaPosts : mediaPosts;
      if (list.some((p) => p.postId === target.postId)) {
        pendingJumpRef.current = target.postId;
        setMomentTab(tab);
        return;
      }
    }
  };

  const toggleCaption = () => setCaptionExpanded((prev) => !prev);

  /* =======================================================
     🎛️ HANDLERS — upload flow
  ======================================================= */

  // Tapping the upload button opens the Photo / Video bottom sheet (same
  // as "Use this sound") — it no longer opens a file picker directly.
  const resetCreateFlow = () => {
    setMomentKind("standard");
    setCreateStep("kind");
    setSeriesDraft({ mode: null, title: "", seriesId: null, season: 1, episode: 1 });
  };

  // The upload button now opens the create sheet at its first screen:
  // Standard / Series / STEM.
  const handleUploadClick = () => {
    if (isUploadingMedia) return;
    resetCreateFlow();
    setShowMediaTypeMenu(true);
  };

  const handleChooseKind = (kind) => {
    setMomentKind(kind);
    setCreateStep(kind === "series" ? "seriesMode" : "media");
  };

  const handleChooseNewSeries = () => {
    setSeriesDraft({ mode: "new", title: "", seriesId: null, season: 1, episode: 1 });
    setCreateStep("seriesNew");
  };

  const handleChooseAddToSeries = () => setCreateStep("seriesPick");

  // Picked one of the viewer's series: default to the next episode of the
  // latest season (the stepper lets them change season / episode).
  const handleChooseExistingSeries = (group) => {
    const lastSeason = group.posts[group.posts.length - 1].seasonNumber || 1;
    setSeriesDraft({
      mode: "existing",
      title: seriesTitleOf(group),
      seriesId: group.seriesId,
      season: lastSeason,
      episode: nextEpisodeNumber(group, lastSeason),
    });
    setCreateStep("seriesNumbers");
  };

  const changeDraftSeason = (group, season) => {
    const maxSeason = (group.seasons[group.seasons.length - 1] || 1) + 1; // +1 = new season
    const next = Math.min(Math.max(season, 1), maxSeason);
    setSeriesDraft((d) => ({
      ...d,
      season: next,
      episode: nextEpisodeNumber(group, next),
    }));
  };

  const changeDraftEpisode = (episode) =>
    setSeriesDraft((d) => ({ ...d, episode: Math.max(1, episode) }));

  const handleCreateBack = () => {
    if (createStep === "seriesMode") return setCreateStep("kind");
    if (createStep === "seriesNew" || createStep === "seriesPick") {
      return setCreateStep("seriesMode");
    }
    if (createStep === "seriesNumbers") return setCreateStep("seriesPick");
    if (createStep === "media") {
      if (momentKind !== "series") return setCreateStep("kind");
      return setCreateStep(seriesDraft.mode === "new" ? "seriesNew" : "seriesNumbers");
    }
    setShowMediaTypeMenu(false);
  };

    const handleChoosePhoto = () => {
    setShowMediaTypeMenu(false);
    setIsPickingFile(true); // keep the video paused while the picker is open
    photoInputRef.current?.click();
  };

  const handleChooseVideo = () => {
    setShowMediaTypeMenu(false);
    setIsPickingFile(true); // keep the video paused while the picker is open
    videoInputRef.current?.click();
  };
  // Core file-intake logic — identical to Feed.js's handleMediaSelect:
  // images get resized before upload, everything is capped at 4 items
  // total, and both `mediaFiles` (raw/resized File objects, what gets
  // uploaded) and `mediaPreview` (object-URL + type, what gets shown in
  // <FeedMediaComposer />) are appended in lockstep. Shared by the
  // Photo/Video FAB inputs below AND FeedMediaComposer's own "📎 Media"
  // picker inside the preview sheet, same as Feed.js.
  const addFiles = async (files) => {
    if (!files.length) return;

    const remainingSlots = 4 - mediaFiles.length;
    if (remainingSlots <= 0) return;

    const limitedFiles = files.slice(0, remainingSlots);
    const processedFiles = [];
    const previews = [];

    for (const file of limitedFiles) {
      if (file.type.startsWith("image/")) {
        const resized = await resizeImage(file, {
          maxSize: 1280,
          quality: 0.8,
          mimeType: "image/jpeg",
        });
        processedFiles.push(resized);
        previews.push({ url: URL.createObjectURL(resized), type: "image" });
      } else {
        processedFiles.push(file);
        previews.push({ url: URL.createObjectURL(file), type: "video" });
      }
    }

    setMediaFiles((prev) => [...prev, ...processedFiles]);
    setMediaPreview((prev) => [...prev, ...previews]);
  };

  // Shared handler for the photo and video <input>s AND for
  // FeedMediaComposer's onSelectMedia — resets the input so picking the
  // same file again still fires onChange, then hands the files to
  // addFiles(). Nothing is uploaded yet; the user confirms with Post.
  const handleMediaSelect = (e) => {
    const files = Array.from(e.target.files || []);
      e.target.value = "";
    // picker is closed; the preview sheet (or nothing, if no files) takes over
    addFiles(files).finally(() => setIsPickingFile(false));
  };

  const handleCancelPending = () => {
    mediaPreview.forEach((item) => {
      if (item.url) URL.revokeObjectURL(item.url);
    });
    setMediaFiles([]);
    setMediaPreview([]);
    setUploadProgress({});
    setPendingCaption("");
    setSelectedSound(null);
    resetCreateFlow();
  };

  // Actually uploads the picked files and creates ONE post carrying all
  // of them as media[] — this only runs when the user taps "Post" in the
  // preview sheet.
  const handlePostPending = async () => {
    if (!mediaFiles.length || isUploadingMedia) return;

    setIsUploadingMedia(true);

    try {
      const uploaded = onUploadMedia
        ? await onUploadMedia(mediaFiles, (fileKey, percent) => {
            setUploadProgress((prev) => ({ ...prev, [fileKey]: percent }));
          })
        : [];

      if (Array.isArray(uploaded) && uploaded.length) {
        const mediaPayload = uploaded.map((item) => ({
          type: item.type,
          url: item.url,
          mimeType: item.mimeType ?? null,
          size: item.size ?? null,
          duration: item.duration ?? 0,
          thumbnail: item.thumbnail ?? null,
          width: item.width ?? null,
          height: item.height ?? null,
          aspectRatio: item.aspectRatio ?? null,
        }));

        // Post category: standard | series | stem
        const isSeriesPost =
          momentKind === "series" &&
          (seriesDraft.mode === "existing"
            ? !!seriesDraft.seriesId
            : seriesDraft.mode === "new" && !!seriesDraft.title.trim());

        const postType = isSeriesPost
          ? "series"
          : momentKind === "stem"
          ? "stem"
          : "standard";

        const seriesId = isSeriesPost
          ? seriesDraft.seriesId || uuidv4()
          : null;

        // Only a brand-new series sends a title; existing ones already have one.
        const seriesTitle =
          isSeriesPost && seriesDraft.mode === "new"
            ? seriesDraft.title.trim()
            : null;

        const newPostId = onCreateMediaPost?.({
          chatId: myChatId,
          authorUsername: myUserName,
          authorDisplayName: myDisplayName,
          authorProfileImageUrl:
            currentUser?.profileImageUrl || avatarUrl,

          // NEW: always an array
          media: mediaPayload,

          // Primary media type for the post
          mediaType: mediaPayload[0]?.type || null,

          caption: pendingCaption,
          text: pendingCaption,

          // Sound chosen from the Sound page (null = original audio)
          sound: toSoundPayload(selectedSound),

          // Post category (+ series fields when type === "series")
          type: postType,
          ...(isSeriesPost
            ? {
                seriesId,
                seriesTitle,
                seasonNumber: seriesDraft.season,
                episodeNumber: seriesDraft.episode,
              }
            : {}),
        });

        // Jump the viewer to the optimistic post (it's prepended at index 0)
        if (postType === "series") {
          if (seriesTitle) {
            setLocalSeriesTitles((prev) => ({ ...prev, [seriesId]: seriesTitle }));
          }
          if (newPostId) {
            setSelectedEpisode((prev) => ({ ...prev, [seriesId]: newPostId }));
            pendingJumpRef.current = newPostId; // lands on it once it is in the list
          }
          setMomentTab("series");
        } else if (postType === "stem") {
          setMomentTab("stem");
        } else {
          setMomentTab("forYou");
        }
        setCurrentIndex(0);
      }

      mediaPreview.forEach((item) => {
        if (item.url) URL.revokeObjectURL(item.url);
      });
      setMediaFiles([]);
      setMediaPreview([]);
      setUploadProgress({});
      setPendingCaption("");
      setSelectedSound(null);
      resetCreateFlow();
    } catch (err) {
      // Upload failed — surfaced via toast elsewhere if the caller wires
      // one up; the preview sheet stays open so the user can retry.
    } finally {
      setIsUploadingMedia(false);
    }
  };

  /* =======================================================
     🖼️ RENDER

     NOTE: the top bar (tab switcher + avatar) below is rendered
     unconditionally, regardless of whether `post` exists. Only the
     media background, bottom-left caption block, and the right-side
     action rail depend on an actual post — those are gated on `post`
     further down. This is what makes the tab switcher / avatar /
     action icons show up even when a feed is still empty.
  ======================================================= */

  return (
    <div
      ref={screenRef}
      className={className}
      style={{
        ...styles.screen,
        ...style,
        touchAction: "none",
        "--moment-inset": `${bottomInset}px`,
      }}
      tabIndex={0}
      onWheel={handleWheel}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onKeyDown={handleKeyDown}
    >
      {/* ---------------------------------------------------
          MEDIA BACKGROUND (or empty placeholder)
      --------------------------------------------------- */}
      {/* Media stage — shrinks and moves up above the comment sheet
          (TikTok-style) while the comments are open. */}
      <div
        style={{
          ...styles.mediaStage,
          ...(showCommentSheet ? styles.mediaStageShrunk : {}),
        }}
      >
      {post && mediaUrl ? (
        mediaType === "video" ? (
          <video
            key={post.postId}
            ref={videoRef}
            src={mediaUrl}
            poster={mediaThumbnail}
            style={styles.media}
            playsInline
            loop
            muted={isMuted || hasCustomSound}
            onClick={handleVideoTap}
            onLoadedData={tryPlay}
            onPlay={() => setIsPaused(false)}
            onPause={() => setIsPaused(true)}
            onLoadedMetadata={(e) => setDuration(e.currentTarget.duration || 0)}
            onDurationChange={(e) => setDuration(e.currentTarget.duration || 0)}
            onTimeUpdate={(e) => {
              const t = e.currentTarget.currentTime;
              if (!isScrubbing) setCurrentTime(t);

              // video looped -> restart the sound too
              if (hasCustomSound && t + 0.5 < lastVideoTimeRef.current) {
                const a = soundAudioRef.current;
                if (a) a.currentTime = postSound?.startTime || 0;
              }
              lastVideoTimeRef.current = t;
            }}
          />
        ) : (
          <img
            src={mediaUrl}
            alt={captionBody || "Moment"}
            style={styles.media}
            onClick={handleVideoTap}
          />
        )
      ) : (
        <div style={styles.emptyMediaBg} />
      )}

      {/* Play indicator while a video is paused */}
      {post && (mediaType === "video" || isPhotoSound) && isPaused && (
        <div style={styles.pausedIcon}>
          <svg width="34" height="34" viewBox="0 0 24 24" fill="#fff">
            <path d="M8 5v14l11-7z" />
          </svg>
        </div>
      )}
      </div>

      {/* Bottom scrim so text/icons stay legible over any media */}
      <div style={{ ...styles.bottomScrim, ...(showCommentSheet ? styles.hiddenWhileComments : {}) }} />
      <div style={{ ...styles.topScrim, ...(showCommentSheet ? styles.hiddenWhileComments : {}) }} />

      {/* Play time + seekable progress bar (video posts only) */}
      {post && (mediaType === "video" || isPhotoSound) && duration > 0 && (
        <div
          style={{ ...styles.progressWrap, ...(showCommentSheet ? styles.hiddenWhileComments : {}) }}
          onTouchStart={(e) => e.stopPropagation()}
          onTouchEnd={(e) => e.stopPropagation()}
          onWheel={(e) => e.stopPropagation()}
          onPointerDown={(e) => {
            e.currentTarget.setPointerCapture(e.pointerId);
            setIsScrubbing(true);
            seekFromEvent(e);
          }}
          onPointerMove={(e) => isScrubbing && seekFromEvent(e)}
          onPointerUp={() => setIsScrubbing(false)}
          onPointerCancel={() => setIsScrubbing(false)}
        >
          <span
            style={{
              ...styles.timeLabel,
              fontSize: isScrubbing ? 18 : 11,
              opacity: isScrubbing ? 1 : 0.85,
            }}
          >
            {formatTime(currentTime)}
          </span>
          <span
            style={{
              ...styles.timeLabel,
              ...styles.timeLabelRight,
              fontSize: isScrubbing ? 18 : 11,
              opacity: isScrubbing ? 1 : 0.85,
            }}
          >
            {formatTime(duration)}
          </span>
          <div
            style={{
              ...styles.progressTrack,
              height: isScrubbing ? 6 : 3,
            }}
          >
            <div
              style={{
                ...styles.progressFill,
                width: `${Math.min((currentTime / duration) * 100, 100)}%`,
              }}
            />
          </div>
        </div>
      )}

      {/* ---------------------------------------------------
          TOP BAR — For You / Following + avatar
          Always visible, even with zero posts loaded.
      --------------------------------------------------- */}
      <div style={{ ...styles.topBar, ...(showCommentSheet ? styles.hiddenWhileComments : {}) }}>
        {/* Row 1 — Metisel label (left) + the VIEWER's avatar (right), same as Feed.js */}
        <div style={styles.avatarRow}>
          <div style={styles.brandLabel}>Metisel</div>

          <button
            type="button"
            onClick={handleMyAvatarClick}
            style={styles.avatarButton}
            aria-label="My profile"
          >
            {myAvatarUrl ? (
              <img src={myAvatarUrl} alt="Me" style={styles.avatarImage} />
            ) : (
              <div style={styles.avatarFallback}>{myInitial}</div>
            )}
          </button>
        </div>

        {/* Row 2 — STEM | For You | Following | Series, equal-width tabs */}
        <div style={styles.tabRow}>
          {MOMENT_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setMomentTab(tab.id)}
              style={{
                ...styles.tabButton,
                ...(momentTab === tab.id
                  ? styles.tabButtonActive
                  : styles.tabButtonInactive),
              }}
            >
              <span style={styles.tabLabel}>
                {tab.label}
                {momentTab === tab.id && <span style={styles.tabUnderline} />}
              </span>
            </button>
          ))}
        </div>
      </div>

      {post ? (
        <>
          {/* ---------------------------------------------------
              BOTTOM-LEFT — author / caption / hashtags / sound
          --------------------------------------------------- */}
          <div style={{ ...styles.bottomLeft, ...(showCommentSheet ? styles.hiddenWhileComments : {}) }}>
            {/* Series tab: series title, season / episode, and < > controls.
                (Swiping left / right does the same thing.) */}
            {seriesNav ? (
              <div style={styles.seriesBadge}>
                <button
                  type="button"
                  onClick={() => goEpisode(-1)}
                  disabled={seriesNav.idx <= 0}
                  style={{
                    ...styles.seriesArrow,
                    opacity: seriesNav.idx <= 0 ? 0.3 : 1,
                  }}
                  aria-label="Previous episode"
                >
                  ‹
                </button>

                <div style={styles.seriesBadgeText}>
                  <span style={styles.seriesTitle}>{seriesNav.title}</span>
                  <span style={styles.seriesMeta}>
                    Season {seriesNav.season} · Episode {seriesNav.episode}
                  </span>
                  {seriesNav.total > 1 && seriesNav.total <= 12 ? (
                    <div style={styles.seriesDots}>
                      {currentSeries.posts.map((p, i) => (
                        <span
                          key={p.postId}
                          style={{
                            ...styles.seriesDot,
                            background:
                              i === seriesNav.idx
                                ? "#fff"
                                : "rgba(255,255,255,0.35)",
                          }}
                        />
                      ))}
                    </div>
                  ) : null}
                </div>

                <button
                  type="button"
                  onClick={() => goEpisode(1)}
                  disabled={seriesNav.idx >= seriesNav.total - 1}
                  style={{
                    ...styles.seriesArrow,
                    opacity: seriesNav.idx >= seriesNav.total - 1 ? 0.3 : 1,
                  }}
                  aria-label="Next episode"
                >
                  ›
                </button>
              </div>
            ) : null}

            <button
              type="button"
              onClick={handleAuthorClick}
              style={styles.authorButton}
            >
              @{authorUsername}
            </button>

            {captionBody ? (
              <p
                onClick={toggleCaption}
                style={{
                  ...styles.caption,
                  WebkitLineClamp: captionExpanded ? "unset" : 2,
                }}
              >
                {captionBody}
              </p>
            ) : null}

            {hashtags.length ? (
              <div style={styles.hashtagRow}>
                {hashtags.map((tag) => (
                  <span key={tag} style={styles.hashtag}>
                    {tag.startsWith("#") ? tag : `#${tag}`}
                  </span>
                ))}
              </div>
            ) : null}

            <button
              type="button"
              onClick={handleSoundClick}
              style={styles.soundRow}
            >
              <MusicIcon size={14} color="#fff" />
              <span style={styles.soundLabel}>{soundLabel}</span>
            </button>
          </div>

          {/* ---------------------------------------------------
              RIGHT ACTION RAIL
          --------------------------------------------------- */}
          <div style={{ ...styles.actionRail, ...(showCommentSheet ? styles.hiddenWhileComments : {}) }}>
            {/* AUTHOR avatar — above the heart */}
            <button
              type="button"
              onClick={handleAvatarClick}
              style={styles.railAvatarButton}
              aria-label={`@${authorUsername}`}
            >
              {authorAvatarUrl ? (
                <img
                  src={authorAvatarUrl}
                  alt={authorUsername}
                  style={styles.avatarImage}
                />
              ) : (
                <div style={styles.avatarFallback}>
                  {authorUsername.slice(0, 1).toUpperCase()}
                </div>
              )}
            </button>

            <ActionButton
              icon={
                <HeartIcon
                  size={32}
                color={(post?.likedByMe ?? isLiked) ? "#ff3b5c" : "#fff"}
                />
              }
              label={formatCount(likesCount)}
              onClick={handleLike}
            />

            <ActionButton
              icon={<CommentIcon size={32} color="#fff" />}
              label={formatCount(commentsCount)}
              onClick={handleComment}
            />

            <ActionButton
              icon={<EyeIcon size={32} color="#fff" />}
              label={formatCount(viewsCount)}
            />

            <ActionButton
              icon={
                <SupportIcon
                  size={32}
                  color={isSupported ? "#ffd15c" : "#fff"}
                />
              }
              label={formatCount(supportsCount)}
              onClick={handleSupport}
            />

            <ActionButton
              icon={<ShareIcon size={32} color="#fff" />}
              label={formatCount(repostsCount)}
              onClick={handleShare}
            />

            {/* Upload / create button + Photo/Video chooser */}
            <div style={{ ...styles.uploadWrap, ...(showCommentSheet ? styles.hiddenWhileComments : {}) }}>
              {/* Spinning sound disc — sits to the LEFT of the upload button
                  (absolutely positioned so the upload button and the rail
                  don't move). */}
              <button
                type="button"
                onClick={handleSoundClick}
                style={styles.soundDiscBeside}
                aria-label="Sound"
              >
                <div style={styles.soundDiscSpin}>
                  <MusicIcon size={18} color="#fff" />
                </div>
              </button>

              <button
                type="button"
                onClick={handleUploadClick}
                disabled={isUploadingMedia}
                style={{
                  ...styles.uploadButton,
                  opacity: isUploadingMedia ? 0.6 : 1,
                }}
              >
                <UploadIcon size={22} color="#fff" />
              </button>
            </div>
          </div>
        </>
      ) : (
        /* ---------------------------------------------------
            EMPTY STATE — no post for the active tab.
            Top bar above still lets the user switch tabs.
        --------------------------------------------------- */
        <div style={styles.emptyOverlay}>
          <p style={styles.emptyText}>{EMPTY_LABELS[momentTab] || "No moments yet."}</p>

          {/* Still offer the upload FAB so an empty feed isn't a dead end */}
          <div style={{ ...styles.uploadWrap, marginTop: 18, ...(showCommentSheet ? styles.hiddenWhileComments : {}) }}>
            <button
              type="button"
              onClick={handleUploadClick}
              disabled={isUploadingMedia}
              style={{
                ...styles.uploadButton,
                opacity: isUploadingMedia ? 0.6 : 1,
              }}
            >
              <UploadIcon size={22} color="#fff" />
            </button>
          </div>
        </div>
      )}

      {/* ---------------------------------------------------
          PHOTO / VIDEO BOTTOM SHEET — opened from the upload button.
          Same look as the chooser on the Sound page. The video pauses
          while it is open (see isActive).
      --------------------------------------------------- */}
      {showMediaTypeMenu ? (() => {
        const draftGroup =
          mySeries.find((g) => g.seriesId === seriesDraft.seriesId) || null;
        const episodeTaken =
          !!draftGroup &&
          draftGroup.posts.some(
            (p) =>
              (p.seasonNumber || 1) === seriesDraft.season &&
              p.episodeNumber === seriesDraft.episode
          );
        const maxSeason = draftGroup
          ? (draftGroup.seasons[draftGroup.seasons.length - 1] || 1) + 1
          : 1;

        const backButton = (
          <button
            type="button"
            onClick={handleCreateBack}
            style={styles.mediaSheetCancel}
          >
            Back
          </button>
        );

        return (
          <div
            style={styles.mediaSheetBackdrop}
            onClick={() => setShowMediaTypeMenu(false)}
            onTouchStart={(e) => e.stopPropagation()}
            onTouchEnd={(e) => e.stopPropagation()}
            onWheel={(e) => e.stopPropagation()}
          >
            <div style={styles.mediaSheet} onClick={(e) => e.stopPropagation()}>
              {/* STEP 1 — what kind of moment? */}
              {createStep === "kind" ? (
                <>
                  <div style={styles.mediaSheetTitle}>Create a Moment</div>
                  <button
                    type="button"
                    onClick={() => handleChooseKind("standard")}
                    style={styles.mediaSheetButtonCol}
                  >
                    <span style={styles.mediaSheetButtonTitle}>Standard</span>
                    <span style={styles.mediaSheetButtonSub}>A regular moment</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleChooseKind("series")}
                    style={styles.mediaSheetButtonCol}
                  >
                    <span style={styles.mediaSheetButtonTitle}>Series</span>
                    <span style={styles.mediaSheetButtonSub}>
                      Episodes grouped into seasons
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleChooseKind("stem")}
                    style={styles.mediaSheetButtonCol}
                  >
                    <span style={styles.mediaSheetButtonTitle}>STEM</span>
                    <span style={styles.mediaSheetButtonSub}>
                      Science, tech, engineering &amp; math
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowMediaTypeMenu(false)}
                    style={styles.mediaSheetCancel}
                  >
                    Cancel
                  </button>
                </>
              ) : null}

              {/* STEP 2 (series) — new series or add to one of mine? */}
              {createStep === "seriesMode" ? (
                <>
                  <div style={styles.mediaSheetTitle}>Series</div>
                  <button
                    type="button"
                    onClick={handleChooseNewSeries}
                    style={styles.mediaSheetButtonCol}
                  >
                    <span style={styles.mediaSheetButtonTitle}>Create new series</span>
                    <span style={styles.mediaSheetButtonSub}>
                      Starts with Season 1 · Episode 1
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={handleChooseAddToSeries}
                    disabled={!mySeries.length}
                    style={{
                      ...styles.mediaSheetButtonCol,
                      opacity: mySeries.length ? 1 : 0.45,
                    }}
                  >
                    <span style={styles.mediaSheetButtonTitle}>Add to series</span>
                    <span style={styles.mediaSheetButtonSub}>
                      {mySeries.length
                        ? "Add a new episode to one of your series"
                        : "You haven't created a series yet"}
                    </span>
                  </button>
                  {backButton}
                </>
              ) : null}

              {/* STEP 3a — name the new series */}
              {createStep === "seriesNew" ? (
                <>
                  <div style={styles.mediaSheetTitle}>New series</div>
                  <input
                    type="text"
                    value={seriesDraft.title}
                    onChange={(e) =>
                      setSeriesDraft((d) => ({ ...d, title: e.target.value }))
                    }
                    placeholder="Series title"
                    maxLength={60}
                    autoFocus
                    style={styles.sheetInput}
                  />
                  <div style={styles.sheetHint}>
                    This moment will be Season 1 · Episode 1
                  </div>
                  <button
                    type="button"
                    onClick={() => setCreateStep("media")}
                    disabled={!seriesDraft.title.trim()}
                    style={{
                      ...styles.sheetPrimary,
                      opacity: seriesDraft.title.trim() ? 1 : 0.45,
                    }}
                  >
                    Continue
                  </button>
                  {backButton}
                </>
              ) : null}

              {/* STEP 3b — pick one of my series */}
              {createStep === "seriesPick" ? (
                <>
                  <div style={styles.mediaSheetTitle}>Add to series</div>
                  <div style={styles.sheetList}>
                    {mySeries.map((g) => (
                      <button
                        key={g.seriesId}
                        type="button"
                        onClick={() => handleChooseExistingSeries(g)}
                        style={styles.mediaSheetButtonCol}
                      >
                        <span style={styles.mediaSheetButtonTitle}>
                          {seriesTitleOf(g)}
                        </span>
                        <span style={styles.mediaSheetButtonSub}>
                          {g.seasons.length}{" "}
                          {g.seasons.length === 1 ? "season" : "seasons"} ·{" "}
                          {g.posts.length}{" "}
                          {g.posts.length === 1 ? "episode" : "episodes"}
                        </span>
                      </button>
                    ))}
                  </div>
                  {backButton}
                </>
              ) : null}

              {/* STEP 4b — choose season / episode of the picked series */}
              {createStep === "seriesNumbers" && draftGroup ? (
                <>
                  <div style={styles.mediaSheetTitle}>{seriesTitleOf(draftGroup)}</div>
                  <Stepper
                    label="Season"
                    value={seriesDraft.season}
                    onDec={() => changeDraftSeason(draftGroup, seriesDraft.season - 1)}
                    onInc={() => changeDraftSeason(draftGroup, seriesDraft.season + 1)}
                    decDisabled={seriesDraft.season <= 1}
                    incDisabled={seriesDraft.season >= maxSeason}
                  />
                  <Stepper
                    label="Episode"
                    value={seriesDraft.episode}
                    onDec={() => changeDraftEpisode(seriesDraft.episode - 1)}
                    onInc={() => changeDraftEpisode(seriesDraft.episode + 1)}
                    decDisabled={seriesDraft.episode <= 1}
                  />
                  {episodeTaken ? (
                    <div style={styles.sheetWarning}>
                      Season {seriesDraft.season} · Episode {seriesDraft.episode}{" "}
                      already exists
                    </div>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => setCreateStep("media")}
                    disabled={episodeTaken}
                    style={{
                      ...styles.sheetPrimary,
                      opacity: episodeTaken ? 0.45 : 1,
                    }}
                  >
                    Continue
                  </button>
                  {backButton}
                </>
              ) : null}

              {/* LAST STEP — Photo / Video (same as before) */}
              {createStep === "media" ? (
                <>
                  <div style={styles.mediaSheetTitle}>
                    {momentKind === "series"
                      ? `Series · Season ${seriesDraft.season} · Episode ${seriesDraft.episode}`
                      : momentKind === "stem"
                      ? "STEM Moment"
                      : "Create a Moment"}
                  </div>
                  <button
                    type="button"
                    onClick={handleChoosePhoto}
                    style={styles.mediaSheetButton}
                  >
                    <PhotoIcon size={20} color="#fff" />
                    <span>Photo</span>
                  </button>
                  <button
                    type="button"
                    onClick={handleChooseVideo}
                    style={styles.mediaSheetButton}
                  >
                    <VideoIcon size={20} color="#fff" />
                    <span>Video</span>
                  </button>
                  {backButton}
                </>
              ) : null}
            </div>
          </div>
        );
      })() : null}

      {/* ---------------------------------------------------
          COMMENT PAGE — opened from the comment icon. Uses mediaComment.js
          (media post flow): one comment icon per comment, nested replies.
      --------------------------------------------------- */}
      {showCommentSheet && post ? (
        <MediaComment
          post={post}
          comments={mediaCommentsByPost[post.postId] || post.comments || []}
          myChatId={myChatId}
          users={users}
          currentUser={currentUser}
          avatarUrl={avatarUrl}
          onBack={handleCloseCommentSheet}
          onAddComment={onAddMediaComment}
          onLikeComment={onLikeMediaComment}
          onSupportComment={onSupportMediaComment}
          onViewCommentEarnings={onViewMediaCommentEarnings}
          onTrackCommentView={onTrackMediaCommentView}
        />
      ) : null}

      {/* ---------------------------------------------------
          SOUND PAGE — opened from the sound row / spinning disc
          ("detail") or from "Add sound" in the preview sheet ("pick").
          Lives in sound.js.
      --------------------------------------------------- */}
      {soundPage ? (
        <Sound
          sound={soundPage.sound}
          mode={soundPage.mode}
          posts={allPosts}
          fixed={soundPage.mode === "pick"}
          onBack={() => setSoundPage(null)}
          onUseSound={handleUseSound}
          onSelectPost={handleSelectSoundPost}
        />
      ) : null}

      {/* ---------------------------------------------------
          PREVIEW SHEET — shown once the user has picked at least
          one photo/video. Uses the same <FeedMediaComposer /> as
          Feed.js (MediaGrid + per-file upload progress bar) so the
          preview looks and behaves identically. Its own "📎 Media"
          picker also lets the user add more files (up to 4) without
          leaving the sheet.
      --------------------------------------------------- */}
      {mediaPreview.length ? (
        <div style={styles.previewOverlay}>
          <div style={styles.previewSheet}>
            <div style={styles.previewHeader}>
              <span style={styles.previewTitle}>
                {mediaPreview.length === 1
                  ? "1 item selected"
                  : `${mediaPreview.length} items selected`}
              </span>
              <button
                type="button"
                onClick={handleCancelPending}
                disabled={isUploadingMedia}
                style={styles.previewCloseButton}
              >
                <CloseIcon size={16} color="#fff" />
              </button>
            </div>

            {/* Media grid is capped + scrollable so it can never push the
                caption box / Post button off-screen below it */}
            <div style={styles.previewMediaScroll}>
              <FeedMediaComposer
                mediaFiles={mediaFiles}
                mediaPreview={mediaPreview}
                uploadProgress={uploadProgress}
                isPosting={isUploadingMedia}
                onSelectMedia={handleMediaSelect}
                showProgress
              />
            </div>

            {/* What this moment will be posted as (series / STEM) */}
            {momentKind === "stem" ? (
              <div style={styles.previewKindChip}>STEM</div>
            ) : momentKind === "series" && seriesDraft.mode ? (
              <div style={styles.previewKindChip}>
                Series · {seriesDraft.title.trim() || "New series"} · S
                {seriesDraft.season} E{seriesDraft.episode}
              </div>
            ) : null}

            {/* Sound for the new post (optional) */}
            <div style={styles.previewSoundRow}>
              <button
                type="button"
                onClick={handleOpenSoundPicker}
                disabled={isUploadingMedia}
                style={styles.previewSoundButton}
              >
                <MusicIcon size={14} color="#fff" />
                <span style={styles.previewSoundLabel}>
                  {selectedSound ? formatSoundLabel(selectedSound) : "Add sound"}
                </span>
              </button>
              {selectedSound ? (
                <button
                  type="button"
                  onClick={() => setSelectedSound(null)}
                  disabled={isUploadingMedia}
                  style={styles.previewSoundRemove}
                  aria-label="Remove sound"
                >
                  <CloseIcon size={14} color="#fff" />
                </button>
              ) : null}
            </div>

            <textarea
              value={pendingCaption}
              onChange={(e) => setPendingCaption(e.target.value)}
              placeholder="Write a caption..."
              disabled={isUploadingMedia}
              style={styles.previewCaptionInput}
              rows={2}
            />

            <div style={styles.previewActions}>
              <button
                type="button"
                onClick={handleCancelPending}
                disabled={isUploadingMedia}
                style={styles.previewCancelButton}
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handlePostPending}
                disabled={isUploadingMedia}
                style={{
                  ...styles.previewPostButton,
                  opacity: isUploadingMedia ? 0.6 : 1,
                }}
              >
                {isUploadingMedia ? "Posting..." : "Post"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Sound track for posts that use a sound (video itself is muted) */}
      {post && hasCustomSound ? (
        <audio
          key={post.postId}
          ref={soundAudioRef}
          src={postSound.url}
          loop
          preload="auto"
          onLoadedMetadata={(e) => {
            if (postSound.startTime) e.currentTarget.currentTime = postSound.startTime;
            if (isPhotoSound) {
              const d = e.currentTarget.duration;
              setDuration(Number.isFinite(d) ? d : 0);
            }
          }}
          onDurationChange={(e) => {
            if (!isPhotoSound) return;
            const d = e.currentTarget.duration;
            setDuration(Number.isFinite(d) ? d : 0);
          }}
          onTimeUpdate={(e) => {
            if (isPhotoSound && !isScrubbing) setCurrentTime(e.currentTarget.currentTime);
          }}
          style={{ display: "none" }}
        />
      ) : null}

      {/* Hidden native file pickers — triggered programmatically from the
          Photo / Video chooser menu above. */}
      <input
        ref={photoInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleMediaSelect}
        style={{ display: "none" }}
      />
      <input
        ref={videoInputRef}
        type="file"
        accept="video/*"
        multiple
        onChange={handleMediaSelect}
        style={{ display: "none" }}
      />
    </div>
  );
}

/* =========================================================
   ➕➖ STEPPER (season / episode picker in the create sheet)
========================================================= */

function Stepper({ label, value, onDec, onInc, decDisabled, incDisabled }) {
  return (
    <div style={styles.stepperRow}>
      <span style={styles.stepperLabel}>{label}</span>
      <div style={styles.stepperControls}>
        <button
          type="button"
          onClick={onDec}
          disabled={decDisabled}
          style={{ ...styles.stepperBtn, opacity: decDisabled ? 0.35 : 1 }}
          aria-label={`Decrease ${label}`}
        >
          −
        </button>
        <span style={styles.stepperValue}>{value}</span>
        <button
          type="button"
          onClick={onInc}
          disabled={incDisabled}
          style={{ ...styles.stepperBtn, opacity: incDisabled ? 0.35 : 1 }}
          aria-label={`Increase ${label}`}
        >
          +
        </button>
      </div>
    </div>
  );
}

/* =========================================================
   🔘 ACTION BUTTON (icon + count, vertical)
========================================================= */

function ActionButton({ icon, label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      style={styles.actionButton}
    >
      <span style={styles.actionIcon}>{icon}</span>
      <span style={styles.actionLabel}>{label}</span>
    </button>
  );
}

/* =========================================================
   🎨 STYLES
========================================================= */

// How far the fixed mobile bottom tab bar overlaps the bottom of this screen.
// Measured at runtime (see useLayoutEffect in Moment) and set as
// --moment-inset on the root div. 0px on desktop / when the tab bar is hidden.
// Everything pinned to the bottom is lifted by this amount.
const TAB_H = "var(--moment-inset, 0px)";

const styles = {
  screen: {
    position: "relative",
    width: "100%",
    height: "100%",
    minHeight: "100dvh",
    overflow: "hidden",
    background: "#000",
    fontFamily: "sans-serif",
  },

  // Wraps the media so the whole thing can be scaled as one piece.
  // Same footprint as the old full-bleed media (inset 0).
  mediaStage: {
    position: "absolute",
    inset: 0,
    transformOrigin: "top center",
    transition:
      "transform 280ms cubic-bezier(0.22, 1, 0.36, 1), border-radius 280ms ease",
    overflow: "hidden",
    willChange: "transform",
  },

  // Comment sheet takes the bottom 68% (see mediaComment.js), so the media
  // scales to ~30% and sits at the top of the remaining 32%.
  mediaStageShrunk: {
    transform: "translateY(12px) scale(0.3)",
    borderRadius: 28, // looks like ~8px after the 0.3 scale
  },

  // Fades out the caption / action rail / top bar / progress bar while the
  // comments are open, and stops them from catching taps.
  hiddenWhileComments: {
    opacity: 0,
    pointerEvents: "none",
    transition: "opacity 160ms ease",
  },

  emptyMediaBg: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    background: "#111",
  },

  emptyOverlay: {
    position: "absolute",
    inset: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },

  emptyText: {
    color: "rgba(255,255,255,0.7)",
    fontSize: 15,
    fontWeight: 600,
    margin: 0,
  },

  media: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },

  pausedIcon: {
    position: "absolute",
    top: "50%",
    left: "50%",
    width: 64,
    height: 64,
    transform: "translate(-50%, -50%)",
    borderRadius: "50%",
    background: "rgba(0,0,0,0.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    pointerEvents: "none", // taps still reach the video to resume
    zIndex: 3,
  },

  progressWrap: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: TAB_H,
    height: 24,
    display: "flex",
    alignItems: "flex-end",
    cursor: "pointer",
    touchAction: "none",
    zIndex: 5,
  },

  timeLabel: {
    position: "absolute",
    left: 16,
    bottom: 8,
    color: "#fff",
    fontWeight: 600,
    textShadow: "0 1px 3px rgba(0,0,0,0.6)",
    pointerEvents: "none",
    transition: "font-size 0.15s",
  },

  timeLabelRight: {
    left: "auto",
    right: 16,
  },

  progressTrack: {
    width: "100%",
    background: "rgba(255,255,255,0.3)",
    transition: "height 0.15s",
  },

  progressFill: {
    height: "100%",
    background: "#fff",
  },

  topScrim: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 140,
    background:
      "linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0) 100%)",
    pointerEvents: "none",
  },

  bottomScrim: {
    position: "absolute",
    bottom: TAB_H,
    left: 0,
    right: 0,
    height: "45%",
    background:
      "linear-gradient(0deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0) 100%)",
    pointerEvents: "none",
  },

  // Column: avatar row on top, tab row underneath (mirrors Feed.js header).
  topBar: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    padding: "12px 16px 0",
    zIndex: 2,
  },

  avatarRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },

  // "Metisel" — same look as Feed.js (text-xl, bold, green-600)
  brandLabel: {
    fontSize: 20,
    fontWeight: 700,
    color: "#16a34a",
    userSelect: "none",
    whiteSpace: "nowrap",
    textShadow: "0 1px 3px rgba(0,0,0,0.45)",
  },

  // Equal-width tabs: For You first, then Following, spaced like Feed.js.
  tabRow: {
    display: "flex",
    alignItems: "center",
    width: "100%",
  },

  tabButton: {
    flex: 1,
    background: "none",
    border: "none",
    padding: "10px 0 14px",
    fontSize: 15, // 4 tabs now
    fontWeight: 700,
    textAlign: "center",
    cursor: "pointer",
  },

  tabLabel: {
    position: "relative",
    display: "inline-block",
  },

  tabUnderline: {
    position: "absolute",
    left: "50%",
    bottom: -8,
    width: "100%",
    height: 2,
    borderRadius: 2,
    background: "#fff",
    transform: "translateX(-50%)",
  },

  tabButtonActive: {
    color: "#fff",
  },

  tabButtonInactive: {
    color: "rgba(255,255,255,0.6)",
  },

  avatarButton: {
    width: 32,
    height: 32,
    borderRadius: "50%",
    padding: 0,
    border: "2px solid rgba(255,255,255,0.85)",
    background: "#333",
    overflow: "hidden",
    cursor: "pointer",
    flexShrink: 0,
  },

  avatarImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },

  avatarFallback: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    fontWeight: 700,
  },

  bottomLeft: {
    position: "absolute",
    left: 16,
    right: 118, // clear of the rail + the sound disc beside upload
    bottom: `calc(34px + ${TAB_H})`, // 34 = 26 + room for the play-time label
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },

  authorButton: {
    alignSelf: "flex-start",
    background: "none",
    border: "none",
    padding: 0,
    color: "#fff",
    fontSize: 16,
    fontWeight: 700,
    cursor: "pointer",
    textShadow: "0 1px 3px rgba(0,0,0,0.4)",
  },

  caption: {
    margin: 0,
    color: "#fff",
    fontSize: 14.5,
    lineHeight: 1.35,
    display: "-webkit-box",
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    cursor: "pointer",
    textShadow: "0 1px 3px rgba(0,0,0,0.4)",
  },

  hashtagRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
  },

  hashtag: {
    color: "#fff",
    fontSize: 14.5,
    fontWeight: 600,
    textShadow: "0 1px 3px rgba(0,0,0,0.4)",
  },

  soundRow: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    background: "none",
    border: "none",
    padding: 0,
    marginTop: 2,
    cursor: "pointer",
  },

  soundLabel: {
    color: "#fff",
    fontSize: 13,
    fontWeight: 500,
    maxWidth: 220,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  actionRail: {
    position: "absolute",
    right: 10,
    bottom: `calc(26px + ${TAB_H})`,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 8, // icon-to-icon spacing (was 18)
  },

  // Author avatar above the heart
  railAvatarButton: {
    width: 46,
    height: 46,
    borderRadius: "50%",
    padding: 0,
    border: "2px solid #fff",
    background: "#333",
    overflow: "hidden",
    cursor: "pointer",
    marginBottom: 4,
    flexShrink: 0,
  },

  actionButton: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 0, // icon <-> count (was 4)
    background: "none",
    border: "none",
    padding: 0,
    cursor: "pointer",
  },

  // display:flex removes the inline-SVG baseline gap under the icon
  actionIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: 0,
  },

  actionLabel: {
    color: "#fff",
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1.1,
    marginTop: 1,
    textShadow: "0 1px 3px rgba(0,0,0,0.4)",
  },

  // Sound disc beside the upload button: 46 (upload) + 10 gap = 56px left
  soundDiscBeside: {
    position: "absolute",
    right: 56,
    top: "50%",
    transform: "translateY(-50%)",
    width: 44,
    height: 44,
    borderRadius: "50%",
    padding: 0,
    border: "none",
    background: "rgba(255,255,255,0.15)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  },

  soundDiscSpin: {
    width: 32,
    height: 32,
    borderRadius: "50%",
    background: "#222",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    animation: "moment-spin 4s linear infinite",
  },

  uploadWrap: {
    position: "relative",
  },

  uploadButton: {
    width: 46,
    height: 46,
    borderRadius: "50%",
    border: "none",
    background: "#1d9bf0",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
  },

  menuBackdrop: {
    position: "fixed",
    inset: 0,
    zIndex: 4,
  },

  // Photo / Video bottom sheet (matches the chooser in sound.js)
  mediaSheetBackdrop: {
    position: "absolute",
    inset: 0,
    zIndex: 100,
    background: "rgba(0,0,0,0.55)",
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "center",
  },
  mediaSheet: {
    width: "100%",
    maxWidth: 560,
    background: "#161616",
    borderTopLeftRadius: 18,
    borderTopRightRadius: 18,
    padding: "16px 16px calc(16px + var(--moment-inset, 0px))",
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  mediaSheetTitle: {
    textAlign: "center",
    fontSize: 14,
    fontWeight: 700,
    color: "#cfcfcf",
    marginBottom: 4,
  },
  mediaSheetButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    padding: "14px 0",
    borderRadius: 14,
    border: "none",
    background: "#232323",
    color: "#fff",
    fontSize: 15,
    fontWeight: 600,
    cursor: "pointer",
  },
  mediaSheetCancel: {
    padding: "12px 0",
    border: "none",
    background: "transparent",
    color: "#9a9a9a",
    fontSize: 14,
    cursor: "pointer",
  },

  mediaTypeMenu: {
    position: "absolute",
    bottom: "calc(100% + 10px)",
    right: 0,
    display: "flex",
    flexDirection: "column",
    gap: 4,
    background: "rgba(28,28,28,0.95)",
    borderRadius: 12,
    padding: 6,
    minWidth: 130,
    boxShadow: "0 4px 16px rgba(0,0,0,0.4)",
    zIndex: 5,
  },

  mediaTypeMenuButton: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    background: "none",
    border: "none",
    color: "#fff",
    fontSize: 14,
    fontWeight: 600,
    padding: "8px 10px",
    borderRadius: 8,
    cursor: "pointer",
    textAlign: "left",
  },

  previewOverlay: {
    position: "fixed", // was "absolute" — pins to the real viewport instead
                       // of just the Moment screen div, so it can sit above
                       // siblings like MobileChatPanel's bottom tab bar
    inset: 0,
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-end", // sheet still docks to the bottom...
    background: "rgba(0,0,0,0.6)",
    zIndex: 1000, // MobileChatPanel's fixed bottom tab bar is z-[60] — this
                  // must stay above it or the tab bar clips the Post/Cancel row
  },

  previewSheet: {
    width: "100%",
    maxHeight: "min(78vh, 520px)", // ...but the sheet itself is shorter now
    background: "#161616",
    borderTopLeftRadius: 18,
    borderTopRightRadius: 18,
    padding: "14px 16px",
    // Reserve space for the bottom tab bar's real height (published as
    // --bottom-tabs-h by MobileChatPanel) plus the safe-area inset, so
    // Cancel/Post never render underneath it even though the overlay now
    // draws on top.
    paddingBottom:
      "calc(18px + env(safe-area-inset-bottom, 0px) + var(--bottom-tabs-h, 0px))",
    display: "flex",
    flexDirection: "column",
    gap: 12,
    boxSizing: "border-box",
    overflow: "hidden", // outer sheet doesn't scroll — only the grid does
  },

  // Wraps FeedMediaComposer so the thumbnail grid is reduced and
  // scrolls internally instead of growing and covering the caption/buttons
  previewMediaScroll: {
    flex: "0 1 auto",
    minHeight: 0,
    maxHeight: 200,
    overflowY: "auto",
  },

  previewCaptionInput: {
    flexShrink: 0, // never gets squeezed out by the grid above it
    width: "100%",
    resize: "none",
    background: "rgba(255,255,255,0.08)",
    border: "1px solid rgba(255,255,255,0.15)",
    borderRadius: 10,
    padding: "10px 12px",
    color: "#fff",
    fontSize: 14,
    fontFamily: "inherit",
    boxSizing: "border-box",
  },

  previewActions: {
    flexShrink: 0, // Post/Cancel row is always pinned visible
    display: "flex",
    gap: 10,
  },

  previewCancelButton: {
    flex: 1,
    padding: "12px 0",
    borderRadius: 24,
    border: "1px solid rgba(255,255,255,0.25)",
    background: "none",
    color: "#fff",
    fontSize: 15,
    fontWeight: 700,
    cursor: "pointer",
  },

  previewSoundRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 10,
  },

  previewSoundButton: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    maxWidth: "100%",
    padding: "7px 12px",
    borderRadius: 18,
    border: "none",
    background: "rgba(255,255,255,0.12)",
    color: "#fff",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },

  previewSoundLabel: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  previewSoundRemove: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    border: "none",
    background: "rgba(255,255,255,0.12)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    flex: "none",
  },

  previewPostButton: {
    flex: 1,
    padding: "12px 0",
    borderRadius: 24,
    border: "none",
    background: "#1d9bf0",
    color: "#fff",
    fontSize: 15,
    fontWeight: 700,
    cursor: "pointer",
  },

  /* ---------- Series tab badge ---------- */
  seriesBadge: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    alignSelf: "flex-start",
    padding: "6px 10px",
    borderRadius: 14,
    background: "rgba(0,0,0,0.4)",
    marginBottom: 2,
  },

  seriesArrow: {
    background: "none",
    border: "none",
    color: "#fff",
    fontSize: 24,
    lineHeight: 1,
    padding: "0 4px",
    cursor: "pointer",
  },

  seriesBadgeText: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    minWidth: 0,
  },

  seriesTitle: {
    color: "#fff",
    fontSize: 13,
    fontWeight: 700,
    maxWidth: 170,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  seriesMeta: {
    color: "rgba(255,255,255,0.8)",
    fontSize: 12,
    fontWeight: 500,
  },

  seriesDots: {
    display: "flex",
    gap: 4,
    marginTop: 2,
  },

  seriesDot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
  },

  /* ---------- Create sheet (kind / series steps) ---------- */
  mediaSheetButtonCol: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 2,
    padding: "12px 0",
    borderRadius: 14,
    border: "none",
    background: "#232323",
    color: "#fff",
    cursor: "pointer",
    flexShrink: 0,
  },

  mediaSheetButtonTitle: {
    fontSize: 15,
    fontWeight: 600,
  },

  mediaSheetButtonSub: {
    fontSize: 12,
    fontWeight: 400,
    color: "#9a9a9a",
  },

  sheetInput: {
    width: "100%",
    boxSizing: "border-box",
    padding: "12px 14px",
    borderRadius: 12,
    border: "1px solid rgba(255,255,255,0.15)",
    background: "rgba(255,255,255,0.08)",
    color: "#fff",
    fontSize: 15,
    fontFamily: "inherit",
  },

  sheetHint: {
    textAlign: "center",
    fontSize: 12.5,
    color: "#9a9a9a",
  },

  sheetWarning: {
    textAlign: "center",
    fontSize: 12.5,
    color: "#ffb4b4",
  },

  sheetList: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    maxHeight: 260,
    overflowY: "auto",
  },

  sheetPrimary: {
    padding: "14px 0",
    borderRadius: 14,
    border: "none",
    background: "#1d9bf0",
    color: "#fff",
    fontSize: 15,
    fontWeight: 700,
    cursor: "pointer",
  },

  stepperRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 14px",
    borderRadius: 14,
    background: "#232323",
    color: "#fff",
  },

  stepperLabel: {
    fontSize: 14,
    fontWeight: 600,
  },

  stepperControls: {
    display: "flex",
    alignItems: "center",
    gap: 14,
  },

  stepperBtn: {
    width: 32,
    height: 32,
    borderRadius: "50%",
    border: "none",
    background: "rgba(255,255,255,0.14)",
    color: "#fff",
    fontSize: 18,
    lineHeight: 1,
    cursor: "pointer",
  },

  stepperValue: {
    minWidth: 24,
    textAlign: "center",
    fontSize: 16,
    fontWeight: 700,
  },

  previewKindChip: {
    flexShrink: 0,
    alignSelf: "flex-start",
    padding: "5px 10px",
    borderRadius: 14,
    background: "rgba(29,155,240,0.2)",
    color: "#7cc4f7",
    fontSize: 12.5,
    fontWeight: 600,
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
};

/* =========================================================
   🌀 KEYFRAMES (spinning sound disc)
   Injected once — safe to call from multiple Moment instances.
========================================================= */

if (
  typeof document !== "undefined" &&
  !document.getElementById("moment-keyframes")
) {
  const styleTag = document.createElement("style");
  styleTag.id = "moment-keyframes";
  styleTag.innerHTML = `
    @keyframes moment-spin {
      from { transform: rotate(0deg); }
      to   { transform: rotate(360deg); }
    }
  `;
  document.head.appendChild(styleTag);
}